import type {
  DependencyMapViewModel,
  DependencyMapViewSettings,
} from './types';

export type TreeDepthFlags = {
  canSetTreeDepth: boolean;
  hasCollapsedNodes: boolean;
};

export const getTreeDepthFlags = ({
  maxTreeDepth,
  treeDepth,
}: {
  maxTreeDepth: number;
  treeDepth: number;
}): TreeDepthFlags => ({
  canSetTreeDepth: maxTreeDepth >= 2,
  hasCollapsedNodes: maxTreeDepth > 0 && treeDepth < maxTreeDepth,
});

/**
 * The degree and depth bounds the dependency map settings controls reason
 * over, and the answers derived from them. Each environment builds its own
 * settings controls dependencies shape on top of this.
 */
export type DependencyMapDegreesState = TreeDepthFlags & {
  referenceParentMaxDepth: number;
  referenceChildMaxDepth: number;
  maxTreeDepth: number;
  canSetParentDegrees: boolean;
  canSetChildDegrees: boolean;
  hasReferenceParents: boolean;
};

type DependencyMapDegreesStateSource = {
  viewSettings: Pick<
    DependencyMapViewSettings,
    'degreesOfParenthood' | 'treeDepth'
  >;
  viewModel: Pick<
    DependencyMapViewModel,
    'referenceParentMaxDepth' | 'referenceChildMaxDepth' | 'maxTreeDepth'
  >;
};

export const toDependencyMapDegreesState = ({
  viewSettings: { degreesOfParenthood, treeDepth },
  viewModel: { referenceParentMaxDepth, referenceChildMaxDepth, maxTreeDepth },
}: DependencyMapDegreesStateSource): DependencyMapDegreesState => ({
  referenceParentMaxDepth,
  referenceChildMaxDepth,
  maxTreeDepth,
  canSetParentDegrees: Boolean(referenceParentMaxDepth),
  canSetChildDegrees: Boolean(referenceChildMaxDepth),
  hasReferenceParents: Boolean(referenceParentMaxDepth && degreesOfParenthood),
  ...getTreeDepthFlags({ maxTreeDepth, treeDepth }),
});
